<template>
  <v-menu v-model="menu" offset-y :close-on-content-click="false">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        icon
        v-on="on"
        v-bind="attrs"
        @click.stop="handleBellClick"
        :aria-expanded="String(menu)"
        :aria-label="$vuetify.lang.t('$vuetify.notifications.title')"
      >
        <v-badge
          v-if="unreadCount > 0 && !suppressBadge"
          color="red"
          overlap
          class="notification-badge"
          :content="displayUnreadCount"
        >
          <v-icon>mdi-bell</v-icon>
        </v-badge>
        <v-icon v-else>mdi-bell-outline</v-icon>
      </v-btn>
    </template>

    <v-card class="notification-card">
      <div class="header d-flex align-center px-3 py-2">
        <div class="left-row d-flex align-center">
          <div class="title-area ml-2">
            <div class="title">{{ $vuetify.lang.t("$vuetify.notifications.title") }}</div>
          </div>
        </div>

        <div class="actions ml-auto d-flex align-center">
          <v-btn
            text
            small
            class="mark-all-read plain-mark-btn"
            :ripple="false"
            @click.stop.prevent="markVisibleNotificationsRead"
            v-show="hasVisibleUnread"
          >
            {{ $vuetify.lang.t("$vuetify.notifications.mark_visible_as_read") }}
          </v-btn>
        </div>
      </div>

      <v-divider></v-divider>

      <v-list two-line class="notification-list">
        <template v-if="loading">
          <div class="skeleton-list px-2 py-2">
            <v-skeleton-loader
              v-for="n in 6"
              :key="n"
              type="list-item-avatar-two-line"
              class="my-2"
            />
          </div>
        </template>

        <template v-else-if="visibleNotifications.length">
          <v-list-item
            v-for="(notification, index) in visibleNotifications"
            :key="notification.id || index"
            class="notification-item"
            @mouseenter="hoveredId = notification.id"
            @mouseleave="hoveredId = null"
            @click="openNotification(notification)"
          >
            <v-list-item-avatar class="notif-avatar">
              <v-icon dense :color="getNotificationIconColor(notification)">
                {{ getNotificationIcon(notification) }}
              </v-icon>
            </v-list-item-avatar>

            <v-list-item-content>
              <span :class="{ 'font-weight-bold': !isRead(notification) }">
                {{ notification.title }}
              </span>
            </v-list-item-content>

            <v-list-item-action class="d-flex flex-column align-center">
              <v-tooltip bottom v-if="!notification.is_read">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    small
                    @click.stop="toggleNotificationReadFromMenu(notification)"
                    :aria-label="!notification.is_read && $vuetify.lang.t('$vuetify.notifications.mark_as_read')"
                    class="dot-btn"
                  >
                    <span v-if="!notification.is_read" class="unread-dot"></span>
                  </v-btn>
                </template>
                <span v-if="!notification.is_read">{{ $vuetify.lang.t("$vuetify.notifications.mark_as_read") }}</span>
              </v-tooltip>

              <div class="subtitle-and-time">
                <div class="time">
                  {{ timeAgo(notification.created_at) }}
                </div>
              </div>
            </v-list-item-action>
          </v-list-item>
        </template>

        <v-list-item v-else class="notification-item no-notifications">
          <v-list-item-content class="text-center">
            <v-icon color="grey lighten-1" size="36">mdi-bell-off</v-icon>
            <div class="no-notification-text">
              {{ $vuetify.lang.t("$vuetify.notifications.no_notifications") }}
            </div>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <div class="view-all px-3 py-2">
        <v-btn
          block
          outlined
          class="btn-flat-border"
          @click="goToAllNotifications"
        >
          {{ $vuetify.lang.t("$vuetify.notifications.view_all") }}
        </v-btn>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import { timeAgo } from "@/helpers/helper";
import {
  ADD_ALERT,
  MARK_NOTIFICATION_READ,
  BULK_UPDATE_NOTIFICATIONS,
  LOAD_DROPDOWN_NOTIFICATIONS,
  UPDATE_LAST_SEEN
} from "@/store/_actiontypes";
import {
  SET_NOTIFICATIONS,
  SET_NOTIFICATION,
  SET_SUPPRESS_BADGE,
  UPDATE_NOTIFICATION,
  UPDATE_DROPDOWN_NOTIFICATION
} from "@/store/_mutationtypes";

export default {
  name: "NotificationMenu",
  data() {
    return {
      hoveredId: null,
      menu: false,
      socketLoadTimer: null,
      prevNotificationsSnapshot: null,
      loading: false,
      hasVisibleUnread: false
    };
  },
  computed: {
    ...mapState("notification", ["notifications", "unreadCount", "suppressBadge", "dropdownNotifications"]),

    visibleNotifications() {
      return (this.dropdownNotifications || []).slice(0, 10);
    },

    displayUnreadCount() {
      if (this.unreadCount > 9) return "9+";
      return this.unreadCount;
    }
  },
  watch: {
    dropdownNotifications: {
      handler() {
        this.recalculateHasVisibleUnread();
      },
      deep: true
    }
  },
  methods: {
    timeAgo,

    ...mapActions("notification", [MARK_NOTIFICATION_READ, BULK_UPDATE_NOTIFICATIONS, LOAD_DROPDOWN_NOTIFICATIONS]),
    ...mapActions("notification", {
      updateLastSeen: UPDATE_LAST_SEEN
    }),
    ...mapMutations("notification", [
      SET_NOTIFICATIONS,
      SET_NOTIFICATION,
      SET_SUPPRESS_BADGE,
      UPDATE_NOTIFICATION,
      UPDATE_DROPDOWN_NOTIFICATION
    ]),

    ...mapActions("alert", [ADD_ALERT]),

    getDefaultDateRange() {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const startOfMonth = `${yyyy}-${mm}-01`;
      const todayStr = `${yyyy}-${mm}-${dd}`;
      return { start: startOfMonth, end: todayStr };
    },

    buildParams() {
      const base = { page: 1, per_page: 10 };
      // const defaults = this.getDefaultDateRange();
      // base.start_date = defaults.start;
      // base.end_date = defaults.end;
      return base;
    },

    recalculateHasVisibleUnread() {
      try {
        const list = this.visibleNotifications || [];
        const anyUnread = list.some(n => {
          if (!n) return false;
          if (typeof n.is_read === "boolean") return !n.is_read;
          return !n.read_at;
        });
        this.hasVisibleUnread = !!anyUnread;
      } catch (_e) {
        this.hasVisibleUnread = false;
      }
    },

    loadNotifications(manual = false) {
      if (this.loading) return Promise.resolve();
      this.loading = true;

      const params = this.buildParams();
      return this.LOAD_DROPDOWN_NOTIFICATIONS(params)
        .then(() => {
          if (manual) {
            this.SET_SUPPRESS_BADGE(true);
          }
          this.recalculateHasVisibleUnread();
        })
        .catch((err) => {
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: err.message,
              color: "error",
            },
            { root: true }
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },

    markNotificationRead(notification) {
      if (!notification) return;
      this.prevNotificationsSnapshot = (this.notifications || []).map(item => ({ ...item }));
      const wasUnread = !this.isRead(notification);
      const optimisticUpdate = { id: notification.id, is_read: !!wasUnread, read_at: wasUnread ? new Date().toISOString() : null };
      this.UPDATE_NOTIFICATION(optimisticUpdate);

      this.MARK_NOTIFICATION_READ({
        notificationId: notification.id,
        isRead: wasUnread
      }).then((updated) => {
        if (updated && updated.id) {
          this.UPDATE_NOTIFICATION(updated);
        }
        this.recalculateHasVisibleUnread();
      }).catch((err) => {
        if (this.prevNotificationsSnapshot) {
          this.SET_NOTIFICATIONS(this.prevNotificationsSnapshot);
          this.prevNotificationsSnapshot = null;
        }
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message: err.message || this.$vuetify.lang.t("$vuetify.notifications.unexpected_error"),
            color: "error",
          },
          { root: true }
        );
        this.recalculateHasVisibleUnread();
      });
    },

    async toggleNotificationReadFromMenu(notification) {
      if (!notification) return;
      const currentlyRead = (typeof notification.is_read === "boolean") ? notification.is_read : !!notification.read_at;
      const targetRead = !currentlyRead;

      this.UPDATE_DROPDOWN_NOTIFICATION({ id: notification.id, is_read: targetRead, read_at: targetRead ? new Date().toISOString() : null });

      try {
        const res = await this.MARK_NOTIFICATION_READ({
          notificationId: notification.id,
          isRead: targetRead
        });

        if (!res) {
          await this.loadNotifications();
        } else {
          this.recalculateHasVisibleUnread();
        }
      } catch {
        await this.loadNotifications();
        this.recalculateHasVisibleUnread();
      }
    },

    async markVisibleNotificationsRead() {
      const visibleList = this.visibleNotifications || [];
      const ids = visibleList.map(notification => notification.id).filter(Boolean);
      if (!ids.length) return;

      this.prevNotificationsSnapshot = (this.notifications || []).map(item => ({ ...item }));

      const readAt = new Date().toISOString();
      visibleList.forEach(visibleNotification => {
        this.UPDATE_DROPDOWN_NOTIFICATION({ id: visibleNotification.id, is_read: true, read_at: readAt });
      });

      try {
        const res = await this.BULK_UPDATE_NOTIFICATIONS({ notificationIds: ids, isRead: true });

        if (!res) {
          if (this.prevNotificationsSnapshot) {
            this.SET_NOTIFICATIONS(this.prevNotificationsSnapshot);
          }
          this.recalculateHasVisibleUnread();
          return;
        }

        const failed = Array.isArray(res.failed_ids) ? res.failed_ids.map(String) : [];
        const notFound = Array.isArray(res.not_found_ids) ? res.not_found_ids.map(String) : [];
        const invalid = Array.isArray(res.invalid_ids) ? res.invalid_ids.map(String) : [];
        const failedAll = [...failed, ...notFound, ...invalid].map(String);

        if (failedAll.length && this.prevNotificationsSnapshot) {
          const patched = (this.notifications || []).map(current => {
            if (failedAll.includes(String(current.id))) {
              const original = this.prevNotificationsSnapshot.find(n => String(n.id) === String(current.id));
              return original ? { ...original } : current;
            }
            return current;
          });
          this.SET_NOTIFICATIONS(patched);
        } else {
          this.prevNotificationsSnapshot = null;
        }

        this.recalculateHasVisibleUnread();
      } catch {
        if (this.prevNotificationsSnapshot) {
          this.SET_NOTIFICATIONS(this.prevNotificationsSnapshot);
        }
        this.recalculateHasVisibleUnread();
      }
    },

    openNotification(notification) {
      if (!notification) return;

      if (!this.isRead(notification)) {
        this.markNotificationRead(notification);
      }

      try {
        this.SET_NOTIFICATION(notification);
      } catch {
        this.UPDATE_NOTIFICATION(notification);
      }

      this.menu = false;

      const target = notification.url
        ? notification.url
        : { name: "notification-details", params: { id: notification.id } };

      const current = this.$route;

      if (typeof target === "string") {
        if (current.path === target) return;
      } else {
        if (
          current.name === target.name &&
          String(current.params.id) === String(target.params?.id)
        ) {
          return;
        }
      }

      this.$router.push(target).catch(err => {
        if (!err || err.name !== "NavigationDuplicated") throw err;
      });
    },

    handleSocketMessage(payload) {
      let parsed = null;
      try {
        if (!payload) return;
        if (typeof payload === "string") {
          parsed = JSON.parse(payload);
        } else if (payload && payload.parsed) {
          parsed = payload.parsed;
        } else if (payload && payload.raw && typeof payload.raw === "string") {
          parsed = JSON.parse(payload.raw);
        }
      } catch {
        // ignore
      }

      if (parsed) {
        if (this.$route && this.$route.name === "notifications") {
          if (this.socketLoadTimer) {
            clearTimeout(this.socketLoadTimer);
          }
          this.socketLoadTimer = setTimeout(() => {
            this.loadNotifications().catch(() => {
              // ignore
            });
            this.updateLastSeen({ last_seen_at: new Date().toISOString() }).catch(() => {});
          }, 300);
          return;
        }

        if (this.socketLoadTimer) {
          clearTimeout(this.socketLoadTimer);
        }

        if (this.menu) {
          this.socketLoadTimer = setTimeout(() => {
            this.loadNotifications().catch(() => {
              // ignore
            });
            this.updateLastSeen({ last_seen_at: new Date()
              .toISOString() })
              .catch(() => {
                // ignore
              });
          }, 300);
          return;
        }
      }
    },

    handleBellClick() {
      if (!this.menu) {
        this.updateLastSeen({ last_seen_at: new Date().toISOString() })
        .catch(() => {
          // ignore
        });
        this.SET_SUPPRESS_BADGE(true);
      }
      this.menu = !this.menu;

      if (this.menu) {
        this.loadNotifications(true).catch(() => {
          // ignore
        });
      }
    },

    goToAllNotifications() {
      this.menu = false;
      const targetName = "notifications";

      if (this.$route && this.$route.name === targetName) return;

      this.$router.push({ name: targetName });
    },

    getNotificationIcon(notification) {
      if (!notification || !notification.type) return "mdi-bell-outline";
      const type = notification.type.toLowerCase();
      if (type.includes("invite")) return "mdi-account-plus-outline";
      if (type.includes("task")) return "mdi-clipboard-text-outline";
      if (type.includes("system")) return "mdi-alert-circle-outline";
      return "mdi-bell-outline";
    },

    getNotificationIconColor(notification) {
      if (!notification || !notification.type) return "#808080";
      const type = notification.type.toLowerCase();
      if (type.includes("invite")) return "#2196F3";
      if (type.includes("task")) return "#4CAF50";
      if (type.includes("system")) return "#FF9800";
      return "#808080";
    },

    isRead(notification) {
      if (!notification) return false;
      if (typeof notification.is_read === "boolean") return notification.is_read;
      return !!notification.read_at;
    }
  },

  mounted() {
    this.recalculateHasVisibleUnread();

    if (this.$socket && typeof this.$socket.on === "function") {
      this._unsubSocket = this.$socket.on("message", (payload) => {
        this.handleSocketMessage(payload);
      });
    }
  },

  beforeDestroy() {
    if (this._unsubSocket) this._unsubSocket();
    if (this.socketLoadTimer) clearTimeout(this.socketLoadTimer);
  },

  created() {
    this.$store.dispatch("notification/LOAD_LAST_SEEN");
  }

};
</script>

<style scoped>
.notification-card {
  width: 360px;
  max-height: 520px;
  overflow: hidden;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
}

.header {
  justify-content: space-between;
}

.header .title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.project-avatar {
  background: #f5f7fa;
}

.actions .mark-all-read {
  color: rgba(0,0,0,0.6);
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
}

.unread-chip {
  cursor: pointer;
  user-select: none;
}

.notification-list {
  max-height: 380px;
  overflow-y: auto;
  padding: 12px;
}

.notification-item {
  margin-bottom: 10px;
  padding: 12px;
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 8px;
  background: #fbfcfd;
  display: flex;
  align-items: flex-start;
  transition: background 0.2s;
}

.notification-item:hover {
  background: #f6f7f9;
}

.notif-avatar {
  background: #f5f7fa;
}

.notification-item .v-list-item__content {
  cursor: pointer;
  padding-right: 8px;
  flex: 1;
}

.subtitle-and-time {
  margin-top: 6px;
}

.message {
  font-size: 13px;
  color: rgba(0,0,0,0.7);
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.time {
  font-size: 12px;
  color: rgba(0,0,0,0.45);
  margin-top: 6px;
}

.view-all {
  padding: 10px;
}

.dot-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 6px !important;
  min-width: 28px !important;
  height: 28px !important;
}

.unread-dot {
  width: 10px;
  height: 10px;
  border: 50%;
  border-radius: 50%;
  background: #1976D2;
  display: inline-block;
  margin: 0;
  vertical-align: middle;
}

.notification-badge ::v-deep .v-badge__badge {
  transform: translateY(-12%) translateX(10%) !important;
}
.btn-flat-border {
  text-transform: none;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
}
.no-notifications {
  justify-content: center;
  align-items: center;
  height: 150px;
  text-align: center;
}

.skeleton-list .v-skeleton-loader {
  border-radius: 8px;
}

.plain-mark-btn {
  text-transform: none !important; 
  font-weight: 500;                    
  font-size: 14px;                      
  color: rgba(0,0,0,0.65) !important;   
  min-width: 0 !important;  
  padding: 0 6px !important; 
  box-shadow: none !important;
  white-space: normal !important;
  word-break: break-word;
  display: inline-block !important;
  max-width: 100% !important;
}

.plain-mark-btn:hover {
  background: transparent !important;
  box-shadow: none !important;
}

.plain-mark-btn .v-btn__content,
.plain-mark-btn .v-btn__content * {
  text-transform: none !important;
}

.plain-mark-btn:hover::before,
.plain-mark-btn:focus::before,
.plain-mark-btn::before {
  background-color: transparent !important;
  opacity: 0 !important;
}

.plain-mark-btn:active {
  background-color: transparent !important;
}

.plain-mark-btn:hover {
  background-color: transparent !important;
}

.no-notification-text {
  color: #777;
  margin-top: 8px;
}

</style>
